import { dashboardApiClient } from './api'

/**
 * Plans and what an organization has, per suite
 * (docs/plans/free-plan-and-reverse-trial-plan.md §3). A suite is what a
 * customer buys; every suite has the same four tiers.
 */

export type Tier = 'free' | 'starter' | 'pro' | 'enterprise'

export const TIER_ORDER: Tier[] = ['free', 'starter', 'pro', 'enterprise']

export interface PlanFeature {
  featureKey: string
  value: any
}

export interface Plan {
  id: string
  name: string
  slug: string
  priceCents: number
  billingCycle: string
  description: string
  suite: string
  suiteName: string
  tier: Tier
  features: PlanFeature[]
}

export interface SuiteGrant {
  kind: 'trial' | 'extension'
  expiresAt: string
  /** True while it still counts. An ended grant is kept so the page can say the trial is over. */
  live: boolean
  daysRemaining: number
}

export interface SuiteSubscription {
  planSlug: string
  tier: Tier
  /** True once Stripe stopped billing it (canceled, unpaid). Ours, so known even when Stripe is not. */
  ended: boolean
  /** Stripe's own status, read live; null when Stripe could not be reached. */
  status: string | null
  periodEndsAt: string | null
  cancelAtPeriodEnd: boolean
  seats: number | null
}

/**
 * Paid for right now. Read from the subscription, never from `source`: a
 * Starter subscription bought during a Pro trial is bought, though the trial
 * still sets the tier until it ends.
 */
export function isPaying(suite: SuitePlan | null | undefined): boolean {
  return !!suite?.subscription && !suite.subscription.ended
}

export interface SuitePlan {
  suite: string
  suiteName: string
  tier: Tier
  planSlug: string
  /** Where the tier comes from: a live subscription, a running grant, or free. */
  source: 'subscription' | 'grant' | 'free'
  grant: SuiteGrant | null
  /** Whether this suite's trial could still be started. */
  trialAvailable: boolean
  subscription: SuiteSubscription | null
  /** People holding an agent seat (Service only; absent for other suites). */
  seatsUsed?: number
  /** Seats the plan allows: the free cap, the seats bought, or null for unlimited (a trial). */
  seatsBought?: number | null
  seatSource?: 'free' | 'subscription' | 'grant' | 'unknown'
  /** The month's AI agent credits (Service only): null = unlimited, 0 = the tier has no AI agent. */
  creditsAllowance?: number | null
  /** Credits counted this month; null when Helpdesk could not be asked. */
  creditsUsed?: number | null
  /** When the pool refills: the first of next month, UTC. */
  creditsResetAt?: string
  /** True when the next AI-worked ticket would not fit: the AI agent is paused. */
  creditsPaused?: boolean
  /** Credits one more seat adds; null when a seat adds none (a trial at its cap). */
  creditsPerSeatAdded?: number | null
  /**
   * True when the organization HAS a subscription but Stripe could not be read.
   * Kept distinct from "no subscription": treating them the same would tell a
   * paying customer they have nothing to cancel.
   */
  billingProviderUnavailable: boolean
}

export interface MyPlan {
  suspended: boolean
  suites: SuitePlan[]
}

export async function getPlans(): Promise<Plan[]> {
  return dashboardApiClient.getPlans()
}

export async function getMyPlan(): Promise<MyPlan> {
  return dashboardApiClient.getMyPlan()
}

export async function changePlan(planSlug: string): Promise<{ planSlug: string }> {
  return dashboardApiClient.changePlan(planSlug)
}

export async function startSuiteTrial(suite: string): Promise<{ suite: string; expiresAt: string }> {
  return dashboardApiClient.startSuiteTrial(suite)
}

/** Set the seats bought on a suite's live subscription; charged now. */
export async function changeSeats(suite: string, seats: number): Promise<{ seats: number; previousSeats: number | null }> {
  return dashboardApiClient.changeSeats(suite, seats)
}
